import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  ProductQrExportsFilterParams,
  ProductQrExportType,
  ProductQrExportsListResponse,
  CreateProductQrExportParams
} from '@/types/product-qr-export'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum } from '@/enums'

import {
  fetchProductQrExports,
  fetchProductQrExport,
  createProductQrExport,
  deleteProductQrExport,
  bulkDeleteProductQrExports
} from '@/lib/api'

type ProductQrExportStateType = {
  data: ProductQrExportType | null
  isLoading: boolean
  error: string | null
}

type ProductQrExportsListStateType = {
  data: ProductQrExportsListResponse | null
  isLoading: boolean
  error: string | null
}

type ProductQrExportsStoreState = {
  productQrExport: ProductQrExportStateType
  productQrExportsList: ProductQrExportsListStateType
  filters: ProductQrExportsFilterParams
  selectedProductQrExportIds: string[]
  setFilters: (filters: Partial<ProductQrExportsFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  fetchProductQrExports: (params?: ProductQrExportsFilterParams) => Promise<ProductQrExportsListResponse>
  resetProductQrExportsList: () => void

  getProductQrExport: (id: string) => Promise<ProductQrExportType>
  createProductQrExport: (params: CreateProductQrExportParams) => Promise<ProductQrExportType>
  deleteProductQrExport: (id: string) => Promise<void>
  bulkDeleteProductQrExports: (ids: string[]) => Promise<BulkDeleteResponse>

  selectProductQrExport: (id: string) => void
  deselectProductQrExport: (id: string) => void
  toggleProductQrExportSelection: (id: string) => void
  selectAllProductQrExports: (productQrExports: ProductQrExportType[]) => void
  deselectAllProductQrExports: () => void
  isProductQrExportSelected: (id: string) => boolean
  resetProductQrExport: () => void
}

const defaultFilters: ProductQrExportsFilterParams = {
  sort: SortEnum.DESC,
  sortBy: 'createdAt',
  q: '',
  createdUsers: undefined,
  updatedUsers: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  updatedAtStart: undefined,
  updatedAtEnd: undefined
}

const useProductQrExportsStore = create<ProductQrExportsStoreState>()(
  devtools((set, get) => ({
    productQrExport: { data: null, isLoading: false, error: null },
    productQrExportsList: { data: null, isLoading: false, error: null },
    selectedProductQrExportIds: [],
    filters: defaultFilters,

    setFilters: filters => set(state => ({ filters: { ...state.filters, ...filters } })),
    resetFilters: () => set({ filters: defaultFilters }),

    updateUrlParams: searchParams => {
      const filters: Partial<ProductQrExportsFilterParams> = {}
      for (const [key, value] of searchParams.entries()) {
        if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'createdUsers' || key === 'updatedUsers') {
          filters[key] = value.split(',')
        } else {
          filters[key as keyof ProductQrExportsFilterParams] = value as any
        }
      }
      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
            params.set(key, value.join(','))
          } else {
            params.set(key, String(value))
          }
        }
      })
      return params
    },

    getProductQrExport: async id => {
      set(state => ({ productQrExport: { ...state.productQrExport, isLoading: true, error: null } }))
      try {
        const data = await fetchProductQrExport(id)
        set(state => ({ productQrExport: { ...state.productQrExport, data, isLoading: false } }))
        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)
        set(state => ({ productQrExport: { ...state.productQrExport, error: errorMessage, isLoading: false } }))
        throw error
      }
    },

    createProductQrExport: async params => {
      set(state => ({ productQrExport: { ...state.productQrExport, isLoading: true, error: null } }))
      try {
        const data = await createProductQrExport(params)
        set(state => ({ productQrExport: { ...state.productQrExport, data, isLoading: false } }))
        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)
        set(state => ({ productQrExport: { ...state.productQrExport, error: errorMessage, isLoading: false } }))
        throw error
      }
    },

    deleteProductQrExport: async id => {
      set(state => ({ productQrExport: { ...state.productQrExport, isLoading: true, error: null } }))
      try {
        await deleteProductQrExport(id)
        set(state => ({ productQrExport: { ...state.productQrExport, isLoading: false } }))
      } catch (error) {
        const errorMessage = getErrorMessage(error)
        set(state => ({ productQrExport: { ...state.productQrExport, error: errorMessage, isLoading: false } }))
        throw error
      }
    },

    resetProductQrExport: () =>
      set(state => ({ productQrExport: { ...state.productQrExport, data: null, error: null } })),

    selectProductQrExport: id =>
      set(state =>
        state.selectedProductQrExportIds.includes(id)
          ? state
          : { selectedProductQrExportIds: [...state.selectedProductQrExportIds, id] }
      ),

    deselectProductQrExport: id =>
      set(state => ({
        selectedProductQrExportIds: state.selectedProductQrExportIds.filter(i => i !== id)
      })),

    toggleProductQrExportSelection: id =>
      set(state => ({
        selectedProductQrExportIds: state.selectedProductQrExportIds.includes(id)
          ? state.selectedProductQrExportIds.filter(i => i !== id)
          : [...state.selectedProductQrExportIds, id]
      })),

    selectAllProductQrExports: productQrExports => set({ selectedProductQrExportIds: productQrExports.map(e => e.id) }),

    deselectAllProductQrExports: () => set({ selectedProductQrExportIds: [] }),

    isProductQrExportSelected: id => get().selectedProductQrExportIds.includes(id),

    bulkDeleteProductQrExports: async ids => {
      set(state => ({ productQrExport: { ...state.productQrExport, isLoading: true, error: null } }))
      try {
        const response = await bulkDeleteProductQrExports({ ids })
        set(state => ({
          productQrExport: { ...state.productQrExport, isLoading: false },
          selectedProductQrExportIds: state.selectedProductQrExportIds.filter(id => !response.deletedItems.includes(id))
        }))
        return response
      } catch (error) {
        const errorMessage = getErrorMessage(error)
        set(state => ({ productQrExport: { ...state.productQrExport, error: errorMessage, isLoading: false } }))
        throw error
      }
    },

    fetchProductQrExports: async (params?: ProductQrExportsFilterParams) => {
      const filtersToUse = params || get().filters
      set(state => ({ productQrExportsList: { ...state.productQrExportsList, isLoading: true, error: null } }))
      try {
        const data = await fetchProductQrExports(filtersToUse)
        set(state => ({ productQrExportsList: { ...state.productQrExportsList, data, isLoading: false } }))
        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)
        set(state => ({
          productQrExportsList: { ...state.productQrExportsList, error: errorMessage, isLoading: false }
        }))
        throw error
      }
    },

    resetProductQrExportsList: () =>
      set(state => ({ productQrExportsList: { ...state.productQrExportsList, data: null, error: null } }))
  }))
)

export default useProductQrExportsStore
